/**
 * 인증 관련 API 함수들
 */

import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

/**
 * 로그인 응답 타입
 */
export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    username: string;
    expiresIn?: number;
  };
}

/**
 * 로그인 요청
 * @param username 사용자명
 * @param password 비밀번호
 * @returns 로그인 결과 (토큰 포함)
 */
export const login = async (username: string, password: string): Promise<LoginResponse> => {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH_LOGIN}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    throw new Error(`Login failed: ${response.status} ${response.statusText}`);
  }

  const data: LoginResponse = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'Login failed');
  }

  return data;
};

/**
 * 로그아웃 요청
 * @param token 인증 토큰
 * @returns 로그아웃 성공 여부
 */
export const logout = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH_LOGOUT}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn('Logout request failed, but continuing with local cleanup');
      return true; // 서버 로그아웃 실패해도 로컬 정리는 진행
    }

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('Logout error:', error);
    return true; // 에러 발생해도 로컬 정리는 진행
  }
};

/**
 * 토큰 유효성 검증
 * @param token 인증 토큰
 * @returns 유효한 토큰 여부
 */
export const validateToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/validate`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    return response.ok;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};
